import { useEffect, useState, useRef } from "react";
import { Send, MessageSquare, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_LABEL = { bot: "Bot", waiting_agent: "En attente", with_agent: "Pris en charge", closed: "Fermé" };
const STATUS_COLOR = { bot: "bg-muted text-ink/60", waiting_agent: "bg-amber-100 text-amber-700", with_agent: "bg-green-100 text-green-700", closed: "bg-ink/10 text-ink/40" };

export default function LiveChat() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef();

  const loadSessions = () => api.get("/admin/chat/sessions").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { loadSessions(); const i = setInterval(loadSessions, 5000); return () => clearInterval(i); }, []);

  useEffect(() => {
    if (!active) return;
    const load = () => api.get(`/admin/chat/${active}`).then((r) => {
      setMessages(r.data.messages);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }).catch(() => {});
    load();
    const i = setInterval(load, 3000);
    return () => clearInterval(i);
  }, [active]);

  const reply = async () => {
    if (!text.trim()) return;
    const content = text; setText("");
    await api.post("/admin/chat/reply", { session_id: active, content });
    api.get(`/admin/chat/${active}`).then((r) => setMessages(r.data.messages));
    loadSessions();
  };
  const close = async () => { await api.post(`/admin/chat/${active}/close`); loadSessions(); };

  const activeSession = sessions.find((s) => s.id === active);

  return (
    <div>
      <h1 className="font-heading text-3xl font-black text-ink">Chat en direct</h1>
      <p className="text-ink/50 mt-1">Répondez aux visiteurs et prenez le relais du bot.</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4 h-[70vh]">
        <div className="bg-card border border-border rounded-xl overflow-y-auto">
          {sessions.map((s) => (
            <button key={s.id} onClick={() => setActive(s.id)} data-testid={`chat-session-${s.id}`}
              className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted/40 ${active === s.id ? "bg-accent" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-ink text-sm">{s.visitor_name}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLOR[s.status]}`}>{STATUS_LABEL[s.status]}</span>
              </div>
              <p className="text-xs text-ink/50 truncate mt-0.5">{s.last_message || "—"}</p>
            </button>
          ))}
          {!sessions.length && <p className="p-6 text-center text-ink/40 text-sm">Aucune conversation.</p>}
        </div>

        <div className="lg:col-span-2 bg-card border border-border rounded-xl flex flex-col">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-ink/40"><MessageSquare size={32} /><p className="mt-2 text-sm">Sélectionnez une conversation</p></div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div><p className="font-heading font-bold text-ink">{activeSession?.visitor_name}</p><span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLOR[activeSession?.status]}`}>{STATUS_LABEL[activeSession?.status]}</span></div>
                <button onClick={close} className="text-xs inline-flex items-center gap-1 text-ink/50 hover:text-red-600"><X size={14} /> Clôturer</button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/30">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "agent" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                      m.role === "agent" ? "bg-primary text-white"
                      : m.role === "user" ? "bg-white border border-border text-ink"
                      : m.role === "system" ? "bg-accent text-ink text-center w-full text-xs"
                      : "bg-ink text-white"}`}>
                      {m.role === "assistant" && <span className="block text-[10px] opacity-60 mb-0.5">Assistant IA</span>}
                      {m.content}
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-border flex gap-2">
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reply()} placeholder="Votre réponse…" className="flex-1 rounded-full border border-border px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary" data-testid="chat-reply-input" />
                <button onClick={reply} data-testid="chat-reply-send" className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center"><Send size={17} /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
